import { useState, useEffect, useMemo } from "react";
import { isAll } from "app/utils/helpers";
import { useTagState } from "app/context/tags";

/**
 * An object containing the tagged data.
 * NOTE: Feel free to add other keys in the object.
 * @typedef {Object} TaggedData
 * @property {string} tag
 * @property {string} name
 * @property {string} image The url to the image
 * @property {Image} logo an imported image
 */

/**
 * Applies effects to a collection of tagged data.
 * See: /src/marketplace/data.js for an example.
 * Requires to be used inside a <TagProvider>
 * @param {Array<TaggedData>} initialData
 * @param {number} batchCount A pagination utility that sets
 *                            the amount of items per load.
 * @param {boolean} limitByCount If true, the data is limited by the batchCount.
 * @return {Object}
 */
export function useTaggedData(initialData, batchCount, limitByCount) {
  const currentTag = useTagState();

  // NOTE: Use `setData` when we refactor this to fetch from a backend.
  const [data, setData] = useState(initialData);
  const [displayCount, setDisplayCount] = useState(batchCount);

  const tags = useMemo(() => {
    return data.reduce((accumulator, current) => {
      const tag = current.tag;
      if (!accumulator.includes(tag)) {
        accumulator.push(tag);
      }
      return accumulator;
    }, []);
  }, [data]);

  useEffect(() => {
    setDisplayCount(batchCount);
  }, [currentTag, setDisplayCount]);

  const currentData = useMemo(() => {
    let _currentData = [...data];
    if (!isAll(currentTag)) {
      _currentData = _currentData.filter((item) => item.tag === currentTag);
    }
    return _currentData;
  }, [data, currentTag]);

  const currentPaginatedData = useMemo(() => {
    let _currentData = [...currentData];
    if (limitByCount) {
      _currentData = _currentData.slice(0, displayCount);
    }
    return _currentData;
  }, [currentData, displayCount]);

  return {
    data, // the raw collection of data
    setData,
    displayCount, // the amount to be displayed at the moment
    setDisplayCount,
    tags, // the unique tags among the data
    currentTag, // the currently selected tag
    currentData, // the current data with all the filters applied
    currentPaginatedData, // the current data with the batch limit applied
  };
}
